import React, { memo, useEffect } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchingLocationRoomsSelector,
  generalLevelsSelector,
  roomCreatedSelector,
  roomDeletedSelector,
  roomLevelUpdatedSelector,
  roomsSelector,
} from 'Containers/RocketScan/RoomsView/Rooms/selectors';
import { RoomContent } from 'Containers/RocketScan';
import { Spinner } from 'Components/Spinner';
import { listLocationRooms } from 'Containers/RocketScan/RoomsView/Rooms/actions';
import { roomSourceStatusUpdatedSelector } from 'Containers/ProjectData/LossData/selectors';

import { EmptyRoomsPlaceholder } from 'Components/RocketScan';
import { setSelectedRooms } from 'Containers/RocketScan/Header/ActionsCenter/actions';
import { RoomProvider } from 'Context/RocketScan';
import { Sketch } from 'Components/Project/Unit/Sketch';
import classes from './rooms.module.css';

interface Props {
  locationId: number;
}

function RoomsContainer({ locationId }: Props) {
  const dispatch = useDispatch();

  // selectors
  const rooms = useSelector(roomsSelector, areEqual);
  const roomLevelUpdated = useSelector(roomLevelUpdatedSelector, areEqual);
  const roomCreated = useSelector(roomCreatedSelector, areEqual);
  const roomDeleted = useSelector(roomDeletedSelector, areEqual);
  const levels = useSelector(generalLevelsSelector, areEqual);
  const fetching = useSelector(fetchingLocationRoomsSelector, areEqual);
  const roomSourceStatusUpdated = useSelector(roomSourceStatusUpdatedSelector, areEqual);

  // initial api call
  useEffect(() => {
    if (locationId && levels.length > 0) {
      dispatch(listLocationRooms(locationId));
    }
  }, [locationId, levels]);

  useEffect(() => {
    if (rooms.length > 0) {
      rooms.forEach((room: any) => {
        const { id, photos, photos_count: photosCount } = room;

        dispatch(
          setSelectedRooms({
            id,
            photos,
            photosCount,
          })
        );
      });
    } else {
      dispatch(setSelectedRooms({}));
    }
  }, [rooms]);

  // refresh the API on room create, room delete or level update
  useEffect(() => {
    if (roomLevelUpdated || roomCreated || roomDeleted || roomSourceStatusUpdated) {
      dispatch(listLocationRooms(locationId));
    }
  }, [roomCreated, roomDeleted, roomLevelUpdated, roomSourceStatusUpdated]);

  const validRooms = rooms.filter((room: any) => room.room_type && room.level);
  const invalidRooms = rooms.filter((room: any) => !room.room_type || !room.level);

  return (
    <div className={classes.roomsBase}>
      {invalidRooms.length > 0 && (
        <div className="alert alert-warning mx-3 mt-2" role="alert">
          {invalidRooms.length} room(s) could not be displayed due to missing data (no level or room type assigned).
          This may have been caused by a project merge. Please reassign levels to the affected rooms.
        </div>
      )}
      {validRooms.length > 0 &&
        validRooms.map((room: any, index: number) => (
          <RoomProvider key={room.id}>
            <RoomContent room={room} isLastItem={index === validRooms.length - 1} />
          </RoomProvider>
        ))}
      {!fetching && <Sketch locationId={locationId} />}
      {!fetching && rooms.length === 0 && <EmptyRoomsPlaceholder />}
      {fetching && <Spinner loading />}
    </div>
  );
}

const RoomsContainerMemo = memo(RoomsContainer, areEqual);

export { RoomsContainerMemo as RoomsContainer };
